import type { Reaction, ReactionChemical } from "../../data/models/schemas";

export const REACTION_TYPE_MAX_LENGTH = 50;

export const normalizeReactionType = (
    reactionType: string | null | undefined
): string => reactionType?.trim() || "";

export const isValidReactionTypeForPersistence = (
    reactionType: string | null | undefined
): boolean => {
    const normalizedReactionType = normalizeReactionType(reactionType);
    return (
        normalizedReactionType.length > 0 &&
        normalizedReactionType.length <= REACTION_TYPE_MAX_LENGTH
    );
};

export type ReactionWithPersistenceMetadata = Reaction & {
    created_at?: string;
    updated_at?: string;
    analysisResults?: unknown;
    properties?: unknown;
};

export type ReactionUpdateRequest = Reaction & { ui_id: string };

const withoutReactionRelationshipIdentity = (
    chemical: ReactionChemical
): ReactionChemical => {
    const payload = { ...chemical };
    delete payload.reaction_ui_id;
    return payload;
};

const mapParticipants = (reaction: Reaction): ReactionChemical[] | undefined =>
    reaction.chemicals?.map(withoutReactionRelationshipIdentity);

/**
 * Creates a POST payload for a new reaction process record.
 *
 * Chemical entity UUIDs are intentionally retained so existing library
 * chemicals can be reused. Reaction identity, relationship identity, derived
 * Reaction SMILES, timestamps, and cached analysis state are intentionally
 * discarded.
 */
export const toReactionCreateRequest = (
    reaction: ReactionWithPersistenceMetadata
): Reaction => {
    const payload = { ...reaction };
    delete payload.ui_id;
    delete payload.reaction_smiles;
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.dataCompletenessScore;
    delete payload.hasConditions;
    delete payload.analysisResults;
    delete payload.properties;

    return {
        ...payload,
        chemicals: mapParticipants(reaction),
    };
};

/**
 * Participants are authoritative for updates, so the client never submits a
 * potentially stale derived Reaction SMILES. The server regenerates it from
 * the submitted participants and returns the hydrated canonical reaction.
 */
export const toReactionUpdateRequest = (
    reaction: ReactionUpdateRequest & ReactionWithPersistenceMetadata
): ReactionUpdateRequest => {
    const payload = { ...reaction };
    delete payload.reaction_smiles;
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.dataCompletenessScore;
    delete payload.hasConditions;
    delete payload.analysisResults;
    delete payload.properties;

    return {
        ...payload,
        ui_id: reaction.ui_id,
        chemicals: mapParticipants(reaction),
    };
};

const participantFingerprint = (
    reaction: Reaction | null | undefined
): string =>
    JSON.stringify(
        (reaction?.chemicals || []).map((chemical) => [
            chemical.ui_id || null,
            chemical.smiles || chemical.chemical?.smiles || null,
            chemical.chemical_type || null,
        ])
    );

/**
 * Prevents an unsaved edited reaction from displaying the previous persisted
 * Reaction SMILES. Metadata-only changes retain the known canonical value.
 */
export const invalidateReactionSmilesWhenChemistryChanges = <
    T extends Reaction,
>(
    previous: Reaction | null | undefined,
    next: T
): T => {
    if (
        previous &&
        participantFingerprint(previous) === participantFingerprint(next)
    ) {
        return next;
    }

    return {
        ...next,
        reaction_smiles: undefined,
    };
};
